import DOMPurify from 'dompurify';

function mergeRelWithTabnabbingMitigation(rel: string | null): string {
  const tokens = (rel ?? '').trim().split(/\s+/).filter(Boolean);
  const lowered = new Set(tokens.map((t) => t.toLowerCase()));
  if (!lowered.has('noopener')) {
    tokens.push('noopener');
  }
  if (!lowered.has('noreferrer')) {
    tokens.push('noreferrer');
  }
  return tokens.join(' ');
}

let didRegisterRichHtmlHooks = false;

function registerRichHtmlSanitizerHooks(): void {
  if (didRegisterRichHtmlHooks) {
    return;
  }
  didRegisterRichHtmlHooks = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName !== 'target') {
      return;
    }
    if (node.tagName !== 'A') {
      data.keepAttr = false;
      return;
    }
    if (data.attrValue.trim().toLowerCase() !== '_blank') {
      data.keepAttr = false;
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A') {
      return;
    }
    if (node.getAttribute('target') !== '_blank') {
      return;
    }
    node.setAttribute('rel', mergeRelWithTabnabbingMitigation(node.getAttribute('rel')));
  });
}

registerRichHtmlSanitizerHooks();

function trimEmptyBoundaryParagraphs(html: string): string {
  const emptyBoundaryParagraphPattern =
    /^\s*<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\u00a0|\s)*<\/p>\s*|\s*<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\u00a0|\s)*<\/p>\s*$/gi;

  let trimmedHtml = html;
  let previousHtml: string;

  do {
    previousHtml = trimmedHtml;
    trimmedHtml = trimmedHtml.replace(emptyBoundaryParagraphPattern, '');
  } while (trimmedHtml !== previousHtml);

  return trimmedHtml;
}

export function htmlToPlainText(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const textContent = doc.body.textContent ?? '';

  return textContent
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function stripHtmlForPresenceCheck(input: string): string {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const textContent = doc.body.textContent ?? '';

  return textContent.replace(/\u00a0/g, '').replace(/&nbsp;/g, '');
}

/**
 * Sanitize HTML from stored rich text before `dangerouslySetInnerHTML`.
 *
 * **Neon + SunEditor (Course Planner):** The editor is `NeonRichTextEditor` from
 * `@ps-refarch-ux/neon`, which merges app `sunEditorOptions` onto Neon's defaults from
 * `getNeonSunEditorOptions()` (see that package's `exported-utils/rte/sun-editor-utils`).
 * Course Planner only overrides height/minHeight in `DescriptionField`, so the effective
 * toolbar matches Neon defaults: bold, italic, underline, font/highlight color, font size,
 * alignment, lists, tables, links, and images — plus inline `style` via
 * `attributesWhitelist: { all: 'style' }`.
 *
 * DOMPurify's built-in **HTML profile** already allows the tags/attributes those features
 * produce (`p`, `span`+style, `table`+`se-*` classes, `img`+`data-*`, `figure`/`figcaption`,
 * `a` with `href`/`rel`/`download`, etc.). The one gap we hit was **`target`** on `<a>`:
 * it is not in DOMPurify's default HTML attribute allowlist, so we add it via `ADD_ATTR`.
 *
 * `target` is restricted to **`_blank` on `<a>` only**, and **`rel` is merged to include
 * `noopener` and `noreferrer`** whenever `target="_blank"` is present, so reverse-tabnabbing
 * is not reintroduced.
 *
 * If a screen passes a **custom** `buttonList` (e.g. `video` / embeds), review SunEditor's
 * output and extend this config only after a security review (e.g. `iframe` is not
 * allowed by default and is intentionally strict).
 */
export function sanitizeRichHtmlForDisplay(html: string): string {
  const sanitizedHtml = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  });

  return trimEmptyBoundaryParagraphs(sanitizedHtml);
}
